import React from "react";
import { Box } from "@mui/material";
import MessageContent from "./MessageContent";
import MessageChart from "./MessageChart";
import MessageFeedback from "./MessageFeedback";
import MessageMetadata from "./MessageMetadata";
import { useSpeech } from "react-text-to-speech";

interface MessageProps {
  msg: any;
  chatId: string | null;
  theme: any;
  copiedMessages: Record<string, boolean>;
  onFeedbackMessage: (chatId: string, msgId: string) => void;
  onCopy: (msgId: string, content: string) => void;
  onRelatedQuestionClick: (question: string) => void;
  models: {
    [key: string]: { name: string; model: string; description: string };
  };
  databases: {
    [key: string]: { name: string; database: string; type: string };
  };
  styles: {
    [key: string]: { name: string; style: string; type: string };
  };
  isGenerating: boolean;
  assistanceMode: string;
}

const Message: React.FC<MessageProps> = ({
  msg,
  chatId,
  theme,
  copiedMessages,
  onFeedbackMessage,
  onCopy,
  onRelatedQuestionClick,
  models,
  databases,
  styles,
  isGenerating,
  assistanceMode,
}) => {
  const containsThai = (text: string): boolean => {
    if (!text) return false;
    const thaiPattern = /[\u0E00-\u0E7F]/;
    return thaiPattern.test(text);
  };

  const determineLanguage = (content: string): string => {
    return containsThai(content) ? "th-TH" : "en-US";
  };

  const { speechStatus, start, stop } = useSpeech({
    text: msg.content,
    lang: determineLanguage(msg.content),
  });
  
  const isUser = msg.sender === 'user';

  const glassmorphismStyle = {
    p: 2,
    borderRadius: '20px',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.2)',
    backdropFilter: 'blur(12px) saturate(150%)',
    '-webkit-backdrop-filter': 'blur(12px) saturate(150%)',
  };

  return (
    <Box
      display="flex"
      justifyContent={isUser ? "flex-end" : "flex-start"}
      mb={2}
      px={{ xs: 1, sm: 2 }}
    >
      <Box
        sx={{
          ...glassmorphismStyle,
          bgcolor: isUser ? 'rgba(88, 101, 242, 0.35)' : 'rgba(70, 70, 70, 0.45)',
          borderTopRightRadius: isUser ? '8px' : '20px',
          borderTopLeftRadius: isUser ? '20px' : '8px',
          maxWidth: {
            xs: "95%",
            sm: "85%",
            md: "75%",
          },
          width: msg.sender === "bot" ? "100%" : "fit-content",
        }}
      >
        <MessageContent
          sender={msg.sender}
          content={msg.content}
          theme={theme}
        />
        {msg.sender === "bot" && (
          <MessageFeedback
            msgId={msg.id}
            chatId={chatId}
            content={msg.content}
            isCopied={copiedMessages[msg.id] || false}
            onFeedbackMessage={onFeedbackMessage}
            onCopy={onCopy}
            models={models}
            model={msg.model_info?.model}
            databases={databases}
            database={msg.database_info || ""}
            styles={styles}
            style={msg.style_info || ""}
            isGenerating={isGenerating}
            speechStatus={speechStatus}
            speechStart={start}
            speechStop={stop}
          />
        )}
        {msg.sender === "bot" && (
            <Box sx={{ mt: 2 }}>
              {msg.chart_data && (
                <MessageChart
                  sender={msg.sender}
                  chart_data={msg.chart_data}
                  theme={theme}
                />
              )}
            {msg.metadata && (
              <MessageMetadata
                metadata={msg.metadata}
                onRelatedQuestionClick={onRelatedQuestionClick}
              />
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Message;